import { GroupCompanyReportsDashboard } from '@shared/api/types';
import SimpleAreaChart from '@shared/components/chart/simple-area-chart';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import Loader from '@shared/components/loader';
import { Card } from '@shared/components/ui/card';
import { ChartConfig } from '@shared/components/ui/chart';
import SimpleSelect from '@shared/components/simple-select';
import { formatTonnes } from '@shared/lib/format';
import { cn, formatNumber } from '@shared/lib/utils';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  BarLegend,
  EmissionsBarList,
  EmissionsBarRow,
  SCOPE_BAR_COLORS,
  ScopeLegend,
} from '../components/emissions-bar-list';
import GroupPage from '../components/group-page';
import { ScopeBreakdownCard } from '../components/scope-breakdown-card';
import { useGroupFilters } from '../hooks/use-group-filters';
import { usePeriodDateRange } from '../hooks/use-period-date-range';
import { buildOrgEmissionsSeries } from '../lib/org-emissions-series';
import { useGetGroupCompanyReportsDashboard } from '@shared/api/hooks/group/dashboard';

const EMISSIONS_CHART_CONFIG = {
  totalEmissionsTonnes: {
    label: 'Emissions',
    color: 'var(--chart-3)',
  },
} satisfies ChartConfig;

/**
 * Stacked-area series for the "Emissions by scope" variant. Ordered
 * Scope 3 → Scope 2 → Scope 1 so the largest series (almost always
 * Scope 3 in the food-and-drink sector) renders at the bottom of the
 * stack and dominates the chart visually.
 */
const SCOPE_CHART_CONFIG = {
  scope3Tonnes: { label: 'Scope 3', color: SCOPE_BAR_COLORS['3'] },
  scope2Tonnes: { label: 'Scope 2', color: SCOPE_BAR_COLORS['2'] },
  scope1Tonnes: { label: 'Scope 1', color: SCOPE_BAR_COLORS['1'] },
} satisfies ChartConfig;

const SCOPE_LEGEND_ITEMS = [
  { label: 'Scope 3', color: SCOPE_BAR_COLORS['3'] },
  { label: 'Scope 2', color: SCOPE_BAR_COLORS['2'] },
  { label: 'Scope 1', color: SCOPE_BAR_COLORS['1'] },
];

/** Variant options for the "Emissions over time" chart. */
const OVER_TIME_VARIANTS = {
  perMonth: 'Emissions per month',
  byScope: 'Emissions by scope',
  byOrganisation: 'Emissions by organisation',
};

export default function GroupCompanyEmissionsPage() {
  const [{ orgs }] = useGroupFilters();
  const { start, end } = usePeriodDateRange();
  const { data, status, isError } = useGetGroupCompanyReportsDashboard({
    organizations: orgs.length ? orgs : undefined,
    dateStart: start ?? undefined,
    dateEnd: end ?? undefined,
  });

  if (isError) throw new Error('Failed to load company emissions dashboard');

  return (
    <GroupPage
      pageKey="companyEmissions"
      lastRefreshedAt={data?.lastRefreshedAt}
    >
      {status === 'loading' && <Loader className="h-96" />}
      {!!data && (
        <div className="flex flex-col gap-8 pb-12">
          <ScopeBreakdownCard
            title="Total reported emissions"
            totalTonnes={data.totalEmissionsTonnes}
            rows={[
              {
                label: 'Scope 1',
                barColor: SCOPE_BAR_COLORS['1'],
                tonnes: data.scope1Tonnes,
                organisationsCount: data.scope1Organisations,
              },
              {
                label: 'Scope 2',
                barColor: SCOPE_BAR_COLORS['2'],
                tonnes: data.scope2Tonnes,
                organisationsCount: data.scope2Organisations,
              },
              {
                label: 'Scope 3',
                barColor: SCOPE_BAR_COLORS['3'],
                tonnes: data.scope3Tonnes,
                organisationsCount: data.scope3Organisations,
              },
            ]}
          />
          <EmissionsOverTimeSection data={data} />
          <EmissionsByOrganisationSection data={data} />
          <EmissionsByCategorySection data={data} />
        </div>
      )}
    </GroupPage>
  );
}

function EmissionsOverTimeSection({
  data,
}: {
  data: GroupCompanyReportsDashboard;
}) {
  const [variant, setVariant] = useState(OVER_TIME_VARIANTS.perMonth);
  const isByScope = variant === OVER_TIME_VARIANTS.byScope;
  const isByOrganisation = variant === OVER_TIME_VARIANTS.byOrganisation;

  const sortedPeriods = useMemo(
    () =>
      [...data.emissionsOverTime].sort((a, b) =>
        a.reportEndDate.localeCompare(b.reportEndDate)
      ),
    [data.emissionsOverTime]
  );

  const chartData = useMemo(
    () =>
      sortedPeriods.map((point) => ({
        ...point,
        label: format(parseISO(point.reportEndDate), 'MMM yy'),
      })),
    [sortedPeriods]
  );

  const orgSeries = useMemo(
    () =>
      buildOrgEmissionsSeries(
        sortedPeriods.map((point) => ({
          label: format(parseISO(point.reportEndDate), 'MMM yy'),
          organisations: point.organisations,
        }))
      ),
    [sortedPeriods]
  );

  const yAxisProps = {
    allowDecimals: false,
    tickFormatter: (value: number) =>
      value.toLocaleString('en-GB', { maximumFractionDigits: 0 }),
    width: 44,
    tickMargin: 4,
  };
  const margin = { top: 8, right: 24, left: 0, bottom: 0 };

  return (
    <DashboardSection
      title="Emissions over time"
      description="Explore your company emissions over time. You can split by scope or organisation."
      actions={
        <SimpleSelect
          className="w-64"
          options={Object.values(OVER_TIME_VARIANTS)}
          value={variant}
          onValueChange={setVariant}
        />
      }
    >
      <Card className="flex flex-col gap-2 p-5 ">
        {isByScope && <BarLegend items={SCOPE_LEGEND_ITEMS} />}
        {isByOrganisation && <BarLegend items={orgSeries.legend} />}
        {/*
          Both charts stay mounted and overlaid; we toggle visibility rather than
          swapping which is rendered. A fresh mount would make Recharts'
          ResponsiveContainer paint blank until it re-measures — `invisible`
          keeps each chart's box dimensions so no re-measure occurs.
        */}
        <div className="relative h-80 w-full">
          <div
            className={cn('absolute inset-0', { invisible: isByOrganisation })}
          >
            <SimpleAreaChart
              chartConfig={
                isByScope ? SCOPE_CHART_CONFIG : EMISSIONS_CHART_CONFIG
              }
              chartData={chartData}
              className="aspect-auto size-full"
              margin={margin}
              nameKey="label"
              dataKey="totalEmissionsTonnes"
              stacked={isByScope}
              reverseTooltipItems={isByScope}
              dot={!isByScope}
              strokeWidth={2}
              fillOpacity={isByScope ? 0.75 : 0.5}
              YAxisProps={yAxisProps}
              tooltipValueFormatter={(value) => formatTonnes(value)}
            />
          </div>
          <div
            className={cn('absolute inset-0', { invisible: !isByOrganisation })}
          >
            <SimpleAreaChart
              chartConfig={orgSeries.chartConfig}
              chartData={orgSeries.chartData}
              className="aspect-auto size-full"
              margin={margin}
              nameKey="label"
              dataKey="label"
              stacked
              reverseTooltipItems
              strokeWidth={2.5}
              fillOpacity={0.7}
              YAxisProps={yAxisProps}
              tooltipValueFormatter={(value) => formatTonnes(value)}
            />
          </div>
        </div>
      </Card>
    </DashboardSection>
  );
}

function EmissionsByOrganisationSection({
  data,
}: {
  data: GroupCompanyReportsDashboard;
}) {
  const rows = useMemo<EmissionsBarRow[]>(
    () =>
      [...data.emissionsByOrganisation]
        .sort(
          (a, b) =>
            (b.totalEmissionsTonnes ?? 0) - (a.totalEmissionsTonnes ?? 0)
        )
        .map((org) => ({
          key: org.organizationUuid ?? org.organizationName,
          label: org.organizationName,
          segments: [
            { color: SCOPE_BAR_COLORS['3'], tonnes: org.scope3Tonnes },
            { color: SCOPE_BAR_COLORS['2'], tonnes: org.scope2Tonnes },
            { color: SCOPE_BAR_COLORS['1'], tonnes: org.scope1Tonnes },
          ],
          readout:
            org.totalEmissionsTonnes != null
              ? formatNumber(org.totalEmissionsTonnes)
              : null,
        })),
    [data.emissionsByOrganisation]
  );

  return (
    <DashboardSection
      title="Emissions by organisation"
      description="Compare emissions across each of your organisations, split by scope."
    >
      <Card className="flex flex-col gap-4 p-5">
        <ScopeLegend />
        <EmissionsBarList
          rows={rows}
          seriesNames={['Scope 3', 'Scope 2', 'Scope 1']}
        />
      </Card>
    </DashboardSection>
  );
}

function EmissionsByCategorySection({
  data,
}: {
  data: GroupCompanyReportsDashboard;
}) {
  const rows = useMemo<EmissionsBarRow[]>(
    () =>
      [...data.emissionsByCategory]
        .sort((a, b) => (b.emissionsTonnes ?? 0) - (a.emissionsTonnes ?? 0))
        .map((category) => ({
          key: `${category.scope}-${category.scope3Category}-${category.categoryName}`,
          label: category.categoryName,
          segments: [
            {
              color: SCOPE_BAR_COLORS[category.scope] ?? SCOPE_BAR_COLORS['3'],
              tonnes: category.emissionsTonnes,
            },
          ],
          readout:
            category.emissionsTonnes != null
              ? formatNumber(category.emissionsTonnes)
              : null,
        })),
    [data.emissionsByCategory]
  );

  return (
    <DashboardSection
      title="Emissions by category"
      description="Compare emissions across each of the GHG categories."
    >
      <Card className="flex flex-col gap-4 p-5">
        <ScopeLegend />
        <EmissionsBarList
          rows={rows}
          seriesNames={['Emissions']}
          labelColumnWidth={220}
        />
      </Card>
    </DashboardSection>
  );
}
